"use client"

import { useMemo, useState } from "react"
import Image from "next/image"
import { useTranslations } from "@/lib/intl/use-translations"
import { Button } from "@/components/ui/button"
import type { InsurersData } from "@/lib/quiz/insurers"
import { cn } from "@/lib/utils"

interface Props {
  /** Insurer catalogue from the CMS (server-fetched, passed down by the Wizard). */
  insurers: InsurersData
  onSelect: (insurerId: string, packageId: string) => void
  onSkip: () => void
  isFocus?: boolean
}

const CheckSvg = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

/**
 * Insurer + supplementary-plan picker. A grid of logo cards (one per insurer)
 * followed by plan "pills" for the chosen insurer — replaces the old pair of
 * native `<select>`s. Logos come from the CMS `media` relation; insurers
 * without a logo fall back to a monogram tile.
 */
export function InsuranceStep({
  insurers,
  onSelect,
  onSkip,
  isFocus = false,
}: Props) {
  const t = useTranslations("quiz")
  const [insurerId, setInsurerId] = useState("")
  const [packageId, setPackageId] = useState("")

  const packages = useMemo(() => {
    if (!insurerId) return []
    return insurers.insurers.find((i) => i.id === insurerId)?.packages ?? []
  }, [insurerId, insurers])

  const canSubmit = Boolean(insurerId && packageId)

  return (
    <div className={cn("space-y-5", isFocus && "mx-auto max-w-md")}>
      <div
        role="listbox"
        aria-label={t("steps.insurance.selectPlaceholder")}
        className="grid grid-cols-2 gap-2"
      >
        {insurers.insurers.map((insurer) => {
          const selected = insurer.id === insurerId
          return (
            <button
              key={insurer.id}
              type="button"
              role="option"
              data-testid="insurer-option"
              aria-selected={selected}
              onClick={() => {
                setInsurerId(insurer.id)
                setPackageId("")
              }}
              className={cn(
                "flex cursor-pointer items-center gap-2.5 rounded-[12px] border bg-white px-3 py-2.5 text-left transition-colors",
                selected
                  ? "border-deep-blue-900 ring-deep-blue-900 ring-1"
                  : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
              )}
            >
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-md bg-neutral-100">
                {insurer.logo ? (
                  <Image
                    src={insurer.logo.url}
                    alt=""
                    width={28}
                    height={28}
                    className="h-7 w-7 object-contain"
                  />
                ) : (
                  <span className="type-label-md font-semibold text-neutral-500">
                    {insurer.name.charAt(0)}
                  </span>
                )}
              </span>
              <span className="type-body-md min-w-0 flex-1 truncate font-medium text-neutral-900">
                {insurer.name}
              </span>
              {selected && (
                <span className="text-deep-blue-900 flex-shrink-0">
                  {CheckSvg}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {insurerId && packages.length > 0 && (
        <div className="space-y-2">
          <p className="type-label-lg text-neutral-500">
            {t("steps.insurance.packagePlaceholder")}
          </p>
          <div className="flex flex-wrap gap-2">
            {packages.map((pkg) => {
              const selected = pkg.id === packageId
              return (
                <button
                  key={pkg.id}
                  type="button"
                  data-testid="plan-pill"
                  aria-pressed={selected}
                  onClick={() => setPackageId(pkg.id)}
                  className={cn(
                    "type-body-md cursor-pointer rounded-[10px] border px-4 py-2 font-medium transition-colors",
                    selected
                      ? "border-deep-blue-900 bg-deep-blue-900 text-white"
                      : "border-neutral-200 bg-white text-neutral-800 hover:border-neutral-300"
                  )}
                >
                  {pkg.name}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {isFocus ? (
        <div className="flex flex-col gap-3 pt-4 sm:flex-row sm:justify-center sm:gap-4">
          <Button variant="outline" onClick={onSkip}>
            {t("steps.insurance.skip")}
          </Button>
          <Button
            onClick={() => onSelect(insurerId, packageId)}
            disabled={!canSubmit}
          >
            {t("next")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center">
          <Button
            onClick={() => onSelect(insurerId, packageId)}
            disabled={!canSubmit}
          >
            {t("next")}
          </Button>
          <button
            onClick={onSkip}
            className="type-body-sm text-neutral-600 underline-offset-2 hover:underline"
          >
            {t("steps.insurance.skip")}
          </button>
        </div>
      )}
    </div>
  )
}
